import React from 'react';
import PropTypes from 'prop-types';
import { EM_DASH, formatNumber } from './queueMonitorFormat';

/**
 * WorkersPanel (v3 contract section 1, 2, 5.3)
 *
 * Ground-truth worker liveness panel fed by the health payload's
 * `metrics.workers` block (additive; written by the v3 backend):
 *
 *   workers: {
 *     alive_total: 102,
 *     by_queue: { default: { alive: 35, expected: 35,
 *                            oldest_heartbeat_age_seconds: 12 }, ... },
 *     heartbeats_warming_up: false
 *   }
 *
 * Renders alive-vs-expected per queue with a per-queue deficit and (when the
 * backend supplies them) the oldest/newest heartbeat age. A queue is flagged
 * red when alive == 0 with expected > 0 (the workers_dead condition) and amber
 * when alive < expected (degraded).
 *
 * Locked empty/honesty states (v3 contract 5.3, "no-heartbeat-data empty state"):
 * - workers block absent (pre-v3 backend / deploy skew) -> renders nothing.
 * - heartbeats_warming_up: true -> a muted "worker heartbeats warming up" note
 *   and the per-queue rows render expected-only (alive shown as warming), never
 *   a false "0 alive" alarm (the alive-vs-expected rule is skipped server-side
 *   while warming; the panel mirrors that honesty - it is a state, not a problem).
 * - by_queue empty AND not warming -> "no heartbeat data" empty message.
 *
 * Heartbeat ages: the v3 contract locks alive/expected per queue but does not
 * lock a per-worker ages payload. This panel renders age columns only when the
 * backend includes optional `oldest_heartbeat_age_seconds` /
 * `newest_heartbeat_age_seconds` per queue; absent -> the age columns are
 * omitted entirely (logged deviation - see DECISIONS_LOG). No fabricated ages.
 */

const KNOWN_QUEUE_ORDER = [
	'default',
	'scraper',
	'low',
	'high',
	'notifications',
	'broadcasts',
];

const sortQueueNames = (names) => {
	const known = KNOWN_QUEUE_ORDER.filter((name) => names.includes(name));
	const unknown = names
		.filter((name) => !KNOWN_QUEUE_ORDER.includes(name))
		.sort((a, b) => a.localeCompare(b));
	return [...known, ...unknown];
};

// Whole seconds -> compact human age ("12s", "3m", "1h 4m"); null -> em dash.
const formatAge = (seconds) => {
	if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) {
		return EM_DASH;
	}
	const s = Math.max(0, Math.floor(seconds));
	if (s < 60) {
		return `${s}s`;
	}
	if (s < 3600) {
		return `${Math.floor(s / 60)}m`;
	}
	const h = Math.floor(s / 3600);
	const m = Math.floor((s % 3600) / 60);
	return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const rowState = (alive, expected, warming) => {
	if (warming) {
		return 'warming';
	}
	if (expected > 0 && alive === 0) {
		return 'dead';
	}
	if (alive < expected) {
		return 'degraded';
	}
	return 'ok';
};

const ROW_CLASS = {
	dead: 'table-danger',
	degraded: 'table-warning',
	ok: '',
	warming: '',
};

const WorkersPanel = ({ workers }) => {
	if (!workers || typeof workers !== 'object') {
		// Pre-v3 backend / deploy skew: render nothing.
		return null;
	}

	const warming = workers.heartbeats_warming_up === true;
	const byQueue =
		workers.by_queue && typeof workers.by_queue === 'object'
			? workers.by_queue
			: {};
	const names = sortQueueNames(Object.keys(byQueue));

	// Do any queues carry the optional heartbeat-age fields?
	const hasAges = names.some((name) => {
		const q = byQueue[name] || {};
		return (
			q.oldest_heartbeat_age_seconds !== undefined ||
			q.newest_heartbeat_age_seconds !== undefined
		);
	});

	const expectedTotal = names.reduce((sum, name) => {
		const q = byQueue[name] || {};
		return sum + (typeof q.expected === 'number' ? q.expected : 0);
	}, 0);

	const aliveTotal =
		typeof workers.alive_total === 'number' ? workers.alive_total : null;

	const headerSummary =
		aliveTotal !== null
			? `${formatNumber(aliveTotal)}/${formatNumber(expectedTotal)} alive`
			: warming
				? 'warming up'
				: EM_DASH;

	return (
		<div className="analytics-card workers-panel mb-4">
			<div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
				<h5 className="mb-0 me-2">
					Workers <span className="text-muted small">(alive vs expected)</span>
				</h5>
				<span className="workers-summary fw-semibold">{headerSummary}</span>
			</div>

			{warming && (
				<div className="text-muted small workers-warming mb-2">
					<i className="bi bi-hourglass-split me-2" aria-hidden="true" />
					worker heartbeats warming up - alive-vs-expected check paused
				</div>
			)}

			{names.length === 0 ? (
				!warming && (
					<p className="text-muted workers-empty mb-1">no heartbeat data</p>
				)
			) : (
				<div className="table-responsive">
					<table className="table table-sm mb-1 workers-table">
						<thead className="table-light">
							<tr>
								<th>Queue</th>
								<th className="text-end">Alive</th>
								<th className="text-end">Expected</th>
								<th className="text-end">Deficit</th>
								{hasAges && <th className="text-end">Oldest beat</th>}
								{hasAges && <th className="text-end">Newest beat</th>}
							</tr>
						</thead>
						<tbody>
							{names.map((name) => {
								const q = byQueue[name] || {};
								const alive =
									typeof q.alive === 'number' ? q.alive : null;
								const expected =
									typeof q.expected === 'number' ? q.expected : 0;
								const deficit = Math.max(
									0,
									expected - (alive !== null ? alive : 0),
								);
								const state = rowState(
									alive !== null ? alive : 0,
									expected,
									warming,
								);
								return (
									<tr
										key={name}
										data-queue={name}
										data-state={state}
										className={ROW_CLASS[state]}
									>
										<td>
											<code className="small">{name}</code>
										</td>
										<td className="text-end">
											{warming ? (
												<span className="text-muted">warming</span>
											) : (
												formatNumber(alive)
											)}
										</td>
										<td className="text-end">{formatNumber(expected)}</td>
										<td className="text-end">
											{warming ? (
												EM_DASH
											) : deficit > 0 ? (
												<span className="text-danger fw-semibold">
													{formatNumber(deficit)}
												</span>
											) : (
												formatNumber(0)
											)}
										</td>
										{hasAges && (
											<td className="text-end">
												{formatAge(q.oldest_heartbeat_age_seconds)}
											</td>
										)}
										{hasAges && (
											<td className="text-end">
												{formatAge(q.newest_heartbeat_age_seconds)}
											</td>
										)}
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			)}
			<div className="text-muted small">
				live worker count per queue from worker heartbeats; a queue with
				expected workers but zero alive is a dead-worker condition
			</div>
		</div>
	);
};

WorkersPanel.propTypes = {
	// health.metrics.workers; null/absent = pre-v3 backend (renders nothing).
	workers: PropTypes.object,
};

WorkersPanel.defaultProps = {
	workers: null,
};

export default WorkersPanel;
